export type ClientPortalModuleValue = 'T' | 'C';

export const CLIENT_PORTAL_MODULE = {
  // Reporting a fault and following what happened to it are one feature: a
  // client who can raise a ticket needs to see its status.
  TECH_SUPPORT: 'T',
  COUNTER_READINGS: 'C',
} as const;

export interface PortalLookupResponse {
  tenant_id: number;
  name: string;
  logo: string | null;
  modules: ClientPortalModuleValue[];
}

export interface PortalContact {
  id: number;
  email: string;
  full_name: string;
  client_name: string;
  modules: ClientPortalModuleValue[];
}

export interface PortalJobEquipment {
  id: number;
  serial: string;
  model_name: string | null;
  producer_name: string | null;
}

export interface PortalAttachment {
  id: number;
  attachment: string;
}

export interface PortalJobTask {
  id: number;
  equipment: PortalJobEquipment | null;
  /** Files sent in for this machine. Empty unless the client raised the job. */
  attachments: PortalAttachment[];
}

export interface PortalJob {
  id: number;
  number: string;
  status: string;
  status_display: string;
  planned_for: string | null;
  finished_at: string | null;
  created_at: string;
  reported_via_portal: boolean;
  /** Planned date while open, finish date once done - decided server-side. */
  display_date: string | null;
  display_date_kind: 'planned' | 'finished';
  is_complaint: boolean;
  tasks: PortalJobTask[];
  description: string | null;
  complaint_for: number | null;
  complaint_for_number: string | null;
  /** Whether the client may still revise or withdraw this report. */
  can_edit: boolean;
}

export interface PortalJobPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: PortalJob[];
}

/** Staff-side: a client contact allowed to sign in to the portal. */
export interface ClientPortalContact {
  id: number;
  client: number;
  email: string;
  full_name: string;
  is_active: boolean;
  last_login_at: string | null;
  created_by: number | null;
  created_by_name: string | null;
  /** Jobs this contact has reported through the portal. */
  reported_jobs_count: number;
  created_at: string;
}

/** Staff-side: this tenant's portal configuration. */
export interface ClientPortalSettings {
  client_portal_enabled: boolean;
  client_portal_modules: ClientPortalModuleValue[];
  portal_slug: string | null;
  portal_url: string | null;
  /** Whether the tenant has a fallback schema for portal-created jobs. */
  has_default_job_numeration_schema: boolean;
}

export interface PortalEquipmentCounter {
  kind: string;
  kind_display: string;
  last_value: string | null;
  last_read_at: string | null;
}

export interface PortalEquipment {
  id: number;
  serial: string;
  model_name: string | null;
  producer_name: string | null;
  address: string | null;
  counters: PortalEquipmentCounter[];
}

/** The ticket form's state: one description for the whole report, and the
 *  machines it concerns, each with its own files. */
export interface PortalTicketDraft {
  description: string;
  machines: { equipmentId: number; attachments: PortalAttachment[] }[];
  complaintFor: number | '';
}
